"use client"

import { useEffect, useState } from "react"
import {
  Box,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Stack,
} from "@mui/material"
import {
  MonetizationOn,
  CreditCard,
  Warning,
  AccountBalance,
  TrendingUp,
  Person,
  PictureAsPdf,
} from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchCashSales,
  fetchBankSales,
  fetchCreditSales,
  fetchDebtClients,
  fetchDailyPriceAchat,
  fetchMonthlyPriceAchat,
  fetchDailySales,
  fetchMonthlySales,
} from "../../redux/actions/sales"
import { fetchAllShops, fetchShopsCapital } from "../../redux/actions/shop"
import {
  fetchDailyPurchasePriceAchat,
  fetchDailyPurchaseSales,
  fetchMonthlyPurchasePriceAchat,
  fetchMonthlyPurchaseSales,
  fetchPurchasesByMethod,
} from "../../redux/actions/purchaseAlimentation"
import moment from "moment"
import { generateDailySalesReport, generateMonthlySalesReport } from "../../utils/reportGenerator"
import { toast } from "react-toastify"
import { fetchDailyDepenses, fetchMonthlyDepenses } from "../../redux/actions/depense"
import { getTotals } from "../../services/API"
import { fetchDailyRetraits, fetchMonthlyRetraits } from "../../redux/actions/retraitDepot"
import { useTheme } from "@mui/material/styles"
import { tokens } from "../../theme"

const Dashboard = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.user)
  const { shops } = useSelector((state) => state.shop)
  const {
    cashSales,
    bankSales,
    creditSales,
    debtClients,
    dailyPriceAchat,
    monthlyPriceAchat,
    dailySales,
    monthlySales,
  } = useSelector((state) => state.sales)
  const { depenses, dailyDepenses, monthlyDepenses, dailyTotalDepenses, monthlyTotalDepenses, loading, error } =
    useSelector((state) => state.depense)
  const {
    cashPurchases,
    creditPurchases,
    dailyPurchasePriceAchat,
    monthlyPurchasePriceAchat,
    dailyPurchaseSales,
    monthlyPurchaseSales,
  } = useSelector((state) => state.purchaseAlimentation)
  const { dailyRetraits, monthlyRetraits } = useSelector((state) => state.retraitDepot)
  const { shopsCapital } = useSelector((state) => state.shop)
  const [selectedShop, setSelectedShop] = useState("")
  const [shopSelected, setShopSelected] = useState("")
  const [stats, setStats] = useState({
    daily: {
      total: 0,
      cash: 0,
      bank: 0,
      credit: 0,
    },
    overall: {
      total: 0,
      cash: 0,
      bank: 0,
      credit: 0,
      pending: 0,
    },
  })

  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  // Filter shops based on type
  const electronicShops = shops.filter((shop) => shop.type === "electronics")
  const alimentationShops = shops.filter((shop) => shop.type === "alimentation")

  useEffect(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      dispatch(fetchAllShops())
      dispatch(fetchShopsCapital())
    }
  }, [dispatch, user])

  useEffect(() => {
    if (selectedShop) {
      dispatch(fetchCashSales(selectedShop))
      dispatch(fetchBankSales(selectedShop))
      dispatch(fetchDailyDepenses(selectedShop))
      dispatch(fetchMonthlyDepenses(selectedShop))
      dispatch(fetchCreditSales(selectedShop))
      dispatch(fetchDebtClients(selectedShop))
      dispatch(fetchDailyPriceAchat(selectedShop))
      dispatch(fetchMonthlyPriceAchat(selectedShop))
      dispatch(fetchDailySales(selectedShop))
      dispatch(fetchMonthlySales(selectedShop))
      dispatch(fetchDailyRetraits(selectedShop))
      dispatch(fetchMonthlyRetraits(selectedShop))
      const selectedShopData = shops.find((s) => s._id === selectedShop)
      setShopSelected(selectedShopData)
      if (selectedShopData?.type === "alimentation") {
        dispatch(fetchPurchasesByMethod("cash", selectedShop))
        dispatch(fetchPurchasesByMethod("credit", selectedShop))
        dispatch(fetchDailyPurchasePriceAchat(selectedShop))
        dispatch(fetchMonthlyPurchasePriceAchat(selectedShop))
        dispatch(fetchDailyPurchaseSales(selectedShop))
        dispatch(fetchMonthlyPurchaseSales(selectedShop))
      }
    }
  }, [dispatch, selectedShop, shops])

  useEffect(() => {
    if (cashSales && bankSales && creditSales) {
      const today = moment().startOf("day")
      const selectedShopData = shops.find((s) => s._id === selectedShop)

      // Calculate daily stats
      const todayCashSales =
        selectedShopData?.type === "alimentation"
          ? [...cashSales, ...cashPurchases]
              .filter((sale) => moment(sale.createdAt).isSame(today, "day"))
              .reduce((sum, sale) => sum + sale.total_amount, 0)
          : cashSales
              .filter((sale) => moment(sale.createdAt).isSame(today, "day"))
              .reduce((sum, sale) => sum + sale.total_amount, 0)

      const todayCreditSales =
        selectedShopData?.type === "alimentation"
          ? [...creditSales, ...creditPurchases]
              .filter((sale) =>
                sale.payment_history.some((payment) => moment(payment.payment_date).isSame(today, "day")),
              )
              .reduce((sum, sale) => sum + sale.amount_paid, 0)
          : creditSales
              .filter((sale) =>
                sale.payment_history.some((payment) => moment(payment.payment_date).isSame(today, "day")),
              )
              .reduce((sum, sale) => sum + sale.amount_paid, 0)

      const todayBankSales = bankSales
        .filter((sale) => moment(sale.createdAt).isSame(today, "day"))
        .reduce((sum, sale) => sum + sale.total_amount, 0)

      // Calculate overall stats
      const totalCashSales =
        selectedShopData?.type === "alimentation"
          ? [...cashSales, ...cashPurchases].reduce((sum, sale) => sum + sale.total_amount, 0)
          : cashSales.reduce((sum, sale) => sum + sale.total_amount, 0)

      const totalCreditSales =
        selectedShopData?.type === "alimentation"
          ? [...creditSales, ...creditPurchases].reduce((sum, sale) => sum + sale.amount_paid, 0)
          : creditSales.reduce((sum, sale) => sum + sale.amount_paid, 0)

      const totalBankSales = bankSales.reduce((sum, sale) => sum + sale.total_amount, 0)

      const totalPendingPayments =
        selectedShopData?.type === "alimentation"
          ? [...creditSales, ...creditPurchases].reduce((sum, sale) => sum + sale.remaining_amount, 0)
          : creditSales.reduce((sum, sale) => sum + sale.remaining_amount, 0)

      setStats({
        daily: {
          total: todayCashSales + todayCreditSales + todayBankSales,
          cash: todayCashSales,
          bank: todayBankSales,
          credit: todayCreditSales,
        },
        overall: {
          total: totalCashSales + totalCreditSales + totalBankSales,
          cash: totalCashSales,
          bank: totalBankSales,
          credit: totalCreditSales,
          pending: totalPendingPayments,
        },
      })
    }
  }, [cashSales, bankSales, creditSales, selectedShop, shops])

  const [totalAlimentation, setTotalAlimentation] = useState(0)
  const [totalElectronicsFirstShop, setTotalElectronicsFirstShop] = useState(0)
  const [totalElectronicsSecondShop, setTotalElectronicsSecondShop] = useState(0)

  const fetchAndUseTotals = async () => {
    try {
      const { totalAlimentation, totalElectronicsFirstShop, totalElectronicsSecondShop } = await getTotals()

      // console.log("Total Alimentation:", totalAlimentation);
      // console.log("Total Electronics (First Shop):", totalElectronicsFirstShop);
      // console.log("Total Electronics (Second Shop):", totalElectronicsSecondShop);
      setTotalAlimentation(totalAlimentation)
      setTotalElectronicsFirstShop(totalElectronicsFirstShop)
      setTotalElectronicsSecondShop(totalElectronicsSecondShop)

      // Use the totals directly in your logic
    } catch (error) {
      console.error("Error fetching totals:", error)
    }
  }

  useEffect(() => {
    fetchAndUseTotals()
  }, [])

  const StatCard = ({ title, value, icon, color }) => (
    <Card
      sx={{
        height: "100%",
        backgroundColor: color,
        color: "white",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
        transition: "transform 0.2s, box-shadow 0.2s",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.15)",
        },
        animation: "slideInUp 0.6s ease-out forwards",
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.85,
                mb: 1,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.75rem",
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                fontSize: "1.5rem",
                lineHeight: 1,
              }}
            >
              {value?.toLocaleString()} <span style={{ fontSize: "0.8rem" }}>Fbu</span>
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: "50%",
              padding: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              animation: "pulse 2s infinite",
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )

  const StatCardDebt = ({ title, value, icon, color }) => (
    <Card
      sx={{
        height: "100%",
        background: color,
        color: "white",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {value?.toLocaleString()} Clients
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              padding: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )

  if (user?.role !== "superadmin" && user?.role !== "supervisor") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography variant="h5">Access Denied</Typography>
      </Box>
    )
  }

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        flexWrap="wrap"
        gap={2}
        sx={{
          animation: "slideInUp 0.6s ease-out",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          Dashboard Aperçu
        </Typography>
        <FormControl sx={{ minWidth: 240 }}>
          <InputLabel>Select Shop</InputLabel>
          <Select value={selectedShop} onChange={(e) => setSelectedShop(e.target.value)} label="Select Shop">
            <MenuItem value="">
              <em>Select a shop</em>
            </MenuItem>
            {/* Electronics Shops */}
            {electronicShops.length > 0 && [
              <MenuItem key="electronics-header" disabled sx={{ color: "text.secondary" }}>
                Electronics Shops
              </MenuItem>,
              ...electronicShops.map((shop) => (
                <MenuItem key={shop._id} value={shop._id}>
                  {shop.name}
                </MenuItem>
              )),
            ]}
            {/* Alimentation Shops */}
            {alimentationShops.length > 0 && [
              <MenuItem key="alimentation-header" disabled sx={{ color: "text.secondary" }}>
                Alimentation Shops
              </MenuItem>,
              ...alimentationShops.map((shop) => (
                <MenuItem key={shop._id} value={shop._id}>
                  {shop.name}
                </MenuItem>
              )),
            ]}
          </Select>
        </FormControl>
      </Box>

      {/* Existing button section with updated styles */}
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<PictureAsPdf />}
            onClick={() => {
              if (!selectedShop) {
                toast.error("Veuillez d'abord sélectionner une boutique")
                return
              }
              const shop = shops.find((s) => s._id === selectedShop)
              const dailyStats = {
                type: shop.type,
                dailySales,
                dailyPurchaseSales,
                dailyPriceAchat,
                dailyPurchaseSales,
                dailyPurchasePriceAchat,
                dailyDepenses: dailyDepenses?.totalAmount || 0,
              }
              generateDailySalesReport(cashSales, bankSales, creditSales, shop.name, dailyStats)
            }}
            sx={{
              backgroundColor: "primary.main",
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.95rem",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: `0 12px 32px rgba(34, 197, 94, 0.4)`,
                transform: "translateY(-2px)",
                backgroundColor: "primary.dark",
              },
            }}
          >
            Rapport du jour
          </Button>

          <Button
            variant="contained"
            startIcon={<PictureAsPdf />}
            onClick={() => {
              if (!selectedShop) {
                toast.error("Veuillez d'abord sélectionner une boutique")
                return
              }
              const shop = shops.find((s) => s._id === selectedShop)
              const monthlyStats = {
                type: shop.type,
                monthlySales,
                monthlyPurchaseSales,
                monthlyPriceAchat,
                monthlyPurchasePriceAchat,
                monthlyDepenses: monthlyDepenses?.totalAmount || 0,
              }
              const capitals = {
                totalElectronicsFirstShop,
                totalElectronicsSecondShop,
                totalAlimentation,
              }
              generateMonthlySalesReport(cashSales, bankSales, creditSales, shop.name, monthlyStats, capitals)
            }}
            sx={{
              backgroundColor: "primary.main",
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.95rem",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: `0 12px 32px rgba(34, 197, 94, 0.4)`,
                transform: "translateY(-2px)",
                backgroundColor: "primary.dark",
              },
            }}
          >
            Rapport du Mois
          </Button>
        </Stack>
      </Box>

      {selectedShop ? (
        <>
          {/* Daily Stats */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
              Ventes d'aujourd'hui
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Ventes totales"
                  value={stats.daily.total}
                  icon={<TrendingUp sx={{ color: "white" }} />}
                  color={colors.primary[600]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Ventes en Cash"
                  value={stats.daily.cash}
                  icon={<MonetizationOn sx={{ color: "white" }} />}
                  color={colors.primary[600]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Ventes bancaires"
                  value={stats.daily.bank}
                  icon={<AccountBalance sx={{ color: "white" }} />}
                  color={colors.greenAccent[500]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Ventes à crédit paye"
                  value={stats.daily.credit}
                  icon={<CreditCard sx={{ color: "white" }} />}
                  color={colors.primary[600]}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Expenses and Interests */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
              Les depenses et interets
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Depense total aujourd'hui"
                  value={dailyDepenses?.totalAmount}
                  icon={<CreditCard sx={{ color: "white" }} />}
                  color={colors.primary[600]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Depense total mensuel"
                  value={monthlyDepenses?.totalAmount}
                  icon={<CreditCard sx={{ color: "white" }} />}
                  color={colors.primary[600]}
                />
              </Grid>
              {shopSelected?.type === "electronics" ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Interet d'aujourd'hui"
                      value={Number(Number(dailySales) - Number(dailyDepenses?.totalAmount))}
                      icon={<MonetizationOn sx={{ color: "white" }} />}
                      color={colors.greenAccent[500]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Interet mensuel"
                      value={Number(Number(monthlySales) - Number(monthlyDepenses?.totalAmount))}
                      icon={<MonetizationOn sx={{ color: "white" }} />}
                      color={colors.greenAccent[500]}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Interet d'aujourd'hui"
                      value={Number(Number(dailyPurchaseSales) - Number(dailyDepenses?.totalAmount))}
                      icon={<MonetizationOn sx={{ color: "white" }} />}
                      color={colors.greenAccent[500]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Interet mensuel"
                      value={Number(Number(monthlyPurchaseSales) - Number(monthlyDepenses?.totalAmount))}
                      icon={<MonetizationOn sx={{ color: "white" }} />}
                      color={colors.greenAccent[500]}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* Overall Stats */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
              Statistiques globales
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <StatCard
                      title="Chiffre d'affaires total"
                      value={stats.overall.total}
                      icon={<TrendingUp sx={{ color: "white" }} />}
                      color={colors.primary[600]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StatCard
                      title="Paiements en attente"
                      value={stats.overall.pending}
                      icon={<Warning sx={{ color: "white" }} />}
                      color={colors.redAccent[500]}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, height: "100%", borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "text.primary" }}>
                    Répartition des paiements
                  </Typography>
                  <List sx={{ pt: 0 }}>
                    <ListItem disableGutters>
                      <ListItemIcon>
                        <MonetizationOn sx={{ color: colors.primary[600] }} />
                      </ListItemIcon>
                      <ListItemText primary="Vente en Cash" secondary={`${stats.overall.cash.toLocaleString()} Fbu`} />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon>
                        <AccountBalance sx={{ color: colors.greenAccent[500] }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Ventes bancaires"
                        secondary={`${stats.overall.bank.toLocaleString()} Fbu`}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon>
                        <CreditCard sx={{ color: colors.redAccent[500] }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Ventes à crédit"
                        secondary={`${stats.overall.credit.toLocaleString()} Fbu`}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Debt Clients Summary */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
              Statut de crédit
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCardDebt
                  title="Total des créditeurs"
                  value={debtClients?.length || 0}
                  icon={<Person sx={{ color: "white" }} />}
                  color={colors.primary[600]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total des créditeurs en attente"
                  value={stats.overall.pending}
                  icon={<Warning sx={{ color: "white" }} />}
                  color={colors.redAccent[500]}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Capital Summary */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
              Les capitaux initiaux
            </Typography>
            <Grid container spacing={3}>
              {shopsCapital?.map((shop) => (
                <Grid item xs={12} sm={6} md={3} key={shop.shopId}>
                  <StatCard
                    title={`Capital dans ${shop.shopName}`}
                    value={shop.totalCapital}
                    icon={<TrendingUp sx={{ color: "white" }} />}
                    color={colors.primary[600]}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total des capitaux"
                  value={shopsCapital?.reduce((total, shop) => total + shop.totalCapital, 0) || 0}
                  icon={<TrendingUp sx={{ color: "white" }} />}
                  color={colors.greenAccent[500]}
                />
              </Grid>
            </Grid>
          </Paper>
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
          sx={{
            animation: "fadeIn 0.8s ease-out",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Veuillez sélectionner une boutique pour voir les statistiques
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default Dashboard
